import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fetchUserOptions, type UserOptionItem } from "@app/api/options/user";

export interface UserFilterOption {
    id: string;
    username: string;
}

export interface UserSelectOption {
    label: string;
    value: string;
}

function resolveUserLabel(item: UserOptionItem): string {
    return item.nick || item.tel || String(item.id);
}

/** 客户端用户下拉（负责人/创建人等，会话内缓存，登录后拉取一次） */
export const useUserOptionsStore = defineStore("userOptions", () => {
    const items = ref<UserOptionItem[]>([]);
    const loaded = ref(false);
    const loading = ref(false);

    const filterOptions = computed<UserFilterOption[]>(() =>
        items.value.map(item => ({
            id: item.id,
            username: resolveUserLabel(item),
        })),
    );

    const selectOptions = computed<UserSelectOption[]>(() =>
        items.value.map(item => ({
            label: resolveUserLabel(item),
            value: item.id,
        })),
    );

    async function fetchOptions(force = false) {
        if (loading.value) return;
        if (loaded.value && !force) return;

        loading.value = true;
        try {
            const data = await fetchUserOptions();
            items.value = data.items ?? [];
            loaded.value = true;
        } catch (e) {
            console.error("[userOptions]", e);
            if (force) items.value = [];
        } finally {
            loading.value = false;
        }
    }

    function fetchIfNeeded() {
        return fetchOptions(false);
    }

    function refresh() {
        return fetchOptions(true);
    }

    function reset() {
        items.value = [];
        loaded.value = false;
        loading.value = false;
    }

    function findById(id: string) {
        return items.value.find(item => item.id === id);
    }

    return { items, loaded, loading, filterOptions, selectOptions, fetchIfNeeded, refresh, reset, findById };
});
